import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from './http';

/**
 * Hooks React Query pour `RatingsAggregator` backend (T1 P1 Stats Hub, PR #132).
 *
 * Source unique de vérité côté frontend pour TOUS les ratings affichés.
 * Remplace progressivement (T4) :
 *  - lectures Zustand directes `user?.lichess_ratings?.*`
 *  - `useChesscomRatings()` (sera `@deprecated` puis supprimé)
 *  - literals hardcodés `1743` / `2144`
 *
 * Hiérarchies respectées : FIDE → Lichess → chess.com, Classique → Rapide → Blitz.
 */

export type FideCadence = 'standard' | 'rapide' | 'blitz';
export type LichessCadence = 'classical' | 'rapid' | 'blitz' | 'bullet' | 'puzzle' | 'puzzleRush';
export type ChesscomCadence = 'rapid' | 'blitz' | 'bullet' | 'tactics' | 'daily';

export interface FideRating {
  value: number;
  rated_at: string; // YYYY-MM-DD
  source_label: string | null;
  source_url: string | null;
}

export interface PlatformRating {
  value: number;
  updated_at: string | null;
}

export interface ConsistencyAlert {
  severity: 'info' | 'warning' | 'critical';
  message: string;
}

export interface RatingsSnapshot {
  fide: Partial<Record<FideCadence, FideRating | null>>;
  lichess: Partial<Record<LichessCadence, PlatformRating | null>>;
  chesscom: Partial<Record<ChesscomCadence, PlatformRating | null>>;
  consistency_check: ConsistencyAlert[];
  updated_at: string;
}

export function useRatings() {
  return useQuery<RatingsSnapshot>({
    queryKey: ['users', 'ratings', 'all'],
    queryFn: () => http.get('/api/v1/users/me/ratings/all'),
    staleTime: 60_000,
  });
}

export interface RatingHistoryPoint {
  rated_at: string;
  value: number;
}

export interface RatingHistoryResponse {
  platform: 'fide' | 'lichess' | 'chesscom';
  cadence: string;
  points: RatingHistoryPoint[];
}

export function useRatingsHistory(
  platform: 'fide' | 'lichess' | 'chesscom',
  cadence: string,
  since?: string,
) {
  const qs = new URLSearchParams({ platform, cadence });
  if (since) qs.set('since', since);
  return useQuery<RatingHistoryResponse>({
    queryKey: ['users', 'ratings', 'history', platform, cadence, since ?? null],
    queryFn: () => http.get(`/api/v1/users/me/ratings/history?${qs.toString()}`),
    staleTime: 5 * 60 * 1000,
  });
}

export interface FideRatingInput {
  rated_at: string;
  type: FideCadence;
  value: number;
  source_url?: string;
  source_label?: string;
  note?: string;
}

export function useInsertFideRating() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: FideRatingInput) =>
      http.post<{ ok: true; id: number }>('/api/v1/users/me/ratings/fide', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users', 'ratings'] });
    },
  });
}

export function useDeleteFideRating() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      http.delete<{ ok: true }>(`/api/v1/users/me/ratings/fide/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users', 'ratings'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Stats Hub V2 (PR #G) — hooks multi-cadence + FFE
// ---------------------------------------------------------------------------

/**
 * Cadence aplatie côté multi-cadence : on tolère l'union FIDE/Lichess/chess.com
 * pour ne pas dupliquer le type par plateforme côté front. Chaque point n'a que
 * les clés réellement remontées par le backend (cf. RatingsAggregator V2).
 */
export type Platform = 'fide' | 'lichess' | 'chesscom';

/**
 * Point multi-cadence pivoté par date. Le backend n'injecte une clé que si la
 * valeur existe (cf. `exactOptionalPropertyTypes` côté front : on déclare donc
 * `number | undefined` plutôt que `number | null`).
 */
export interface RatingsHistoryMultiPoint {
  rated_at: string;
  // FIDE
  standard?: number;
  rapide?: number;
  // Lichess
  classical?: number;
  rapid?: number;
  bullet?: number;
  puzzle?: number;
  puzzleRush?: number;
  storm?: number;
  // chess.com
  tactics?: number;
  daily?: number;
  // Cadence commune (FIDE + Lichess + chess.com)
  blitz?: number;
}

export interface RatingsHistoryMultiResponse {
  platform: Platform;
  cadences: string[];
  points: RatingsHistoryMultiPoint[];
}

export function useRatingsHistoryMulti(platform: Platform, since?: string) {
  const qs = new URLSearchParams({ platform });
  if (since) qs.set('since', since);
  return useQuery<RatingsHistoryMultiResponse>({
    queryKey: ['users', 'ratings', 'history-multi', platform, since ?? null],
    queryFn: () => http.get(`/api/v1/users/me/ratings/history-multi?${qs.toString()}`),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Une ligne par mois (DESC). Les clés `<cadence>_delta` peuvent être `null` si
 * un mois manque (close du mois M ou M-1 absente) — on garde `null` côté API
 * pour cohérence backend (cf. `RatingsAggregator::monthlyDeltas`).
 */
export interface MonthlyDeltaRow {
  month: string;
  standard_delta?: number | null;
  rapide_delta?: number | null;
  classical_delta?: number | null;
  rapid_delta?: number | null;
  blitz_delta?: number | null;
  bullet_delta?: number | null;
  puzzle_delta?: number | null;
  puzzleRush_delta?: number | null;
  storm_delta?: number | null;
  tactics_delta?: number | null;
  daily_delta?: number | null;
}

export interface MonthlyDeltasResponse {
  platform: Platform;
  cadences: string[];
  months: number;
  rows: MonthlyDeltaRow[];
}

export function useMonthlyDeltas(platform: Platform, months = 12) {
  const qs = new URLSearchParams({ platform, months: String(months) });
  return useQuery<MonthlyDeltasResponse>({
    queryKey: ['users', 'ratings', 'monthly-deltas', platform, months],
    queryFn: () => http.get(`/api/v1/users/me/ratings/monthly-deltas?${qs.toString()}`),
    staleTime: 5 * 60 * 1000,
  });
}

export interface WdlEntry {
  wins: number;
  draws: number;
  losses: number;
  total: number;
}

export interface FfeWdlByColor {
  white: WdlEntry;
  black: WdlEntry;
  all: WdlEntry;
}

export interface FfeStatsResponse {
  total: FfeWdlByColor;
  standard: FfeWdlByColor;
  rapide: FfeWdlByColor;
  blitz: FfeWdlByColor;
}

export function useFfeStats(since?: string) {
  const qs = new URLSearchParams();
  if (since) qs.set('since', since);
  const query = qs.toString();
  return useQuery<FfeStatsResponse>({
    queryKey: ['users', 'ffe', 'stats', since ?? null],
    queryFn: () =>
      http.get(`/api/v1/users/me/ffe/stats${query ? `?${query}` : ''}`),
    staleTime: 5 * 60 * 1000,
  });
}

export type FfeTimeControl = 'standard' | 'rapide' | 'blitz';

export interface FfeTournament {
  id: number;
  ffe_external_id: string;
  name: string;
  location: string | null;
  organizer: string | null;
  started_at: string;
  ended_at: string | null;
  time_control: FfeTimeControl;
  rounds_played: number | null;
  score: number | null;
  perf: number | null;
  elo_variation: number | null;
  url: string | null;
}

export interface FfeTournamentsResponse {
  tournaments: FfeTournament[];
}

export function useFfeTournaments(since?: string, limit = 20) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (since) qs.set('since', since);
  return useQuery<FfeTournamentsResponse>({
    queryKey: ['users', 'ffe', 'tournaments', since ?? null, limit],
    queryFn: () => http.get(`/api/v1/users/me/ffe/tournaments?${qs.toString()}`),
    staleTime: 5 * 60 * 1000,
  });
}

export interface TopOpponent {
  name: string;
  fide_elo: number | null;
  games_played: number;
  score: number;
  wins: number;
  draws: number;
  losses: number;
  elo_delta_avg: number | null;
}

export interface TopOpponentsResponse {
  opponents: TopOpponent[];
}

export function useTopOpponents(limit = 10) {
  const qs = new URLSearchParams({ limit: String(limit) });
  return useQuery<TopOpponentsResponse>({
    queryKey: ['users', 'ffe', 'opponents', limit],
    queryFn: () => http.get(`/api/v1/users/me/ffe/opponents?${qs.toString()}`),
    staleTime: 5 * 60 * 1000,
  });
}
